export interface FAQItem {
  question: string;
  answer: string;
}

export const faqData: FAQItem[] = [
  {
    question: "What is the Pomodoro Technique?",
    answer: "The Pomodoro Technique is a time management method developed by Francesco Cirillo in the late 1980s. It involves working in focused 25-minute intervals called 'Pomodoros,' followed by short 5-minute breaks. After completing 4 Pomodoros, you take a longer 15-30 minute break. This technique helps improve focus, reduce mental fatigue, and boost productivity by breaking work into manageable chunks."
  },
  {
    question: "How long should Pomodoro breaks be?",
    answer: "Short breaks between Pomodoros should be 5 minutes long, giving you just enough time to rest your mind without losing momentum. After every 4 Pomodoros (about 2 hours of work), take a longer break of 15-30 minutes. This longer break helps prevent burnout and allows your brain to fully recharge for the next work cycle."
  },
  {
    question: "What should I do during Pomodoro breaks?",
    answer: "Use short breaks to step away from your work completely. Try light stretching, deep breathing, looking out a window, grabbing water, or doing simple movements. Avoid checking emails, social media, or other work-related tasks. For longer breaks, you can take a walk, have a snack, meditate, or do something completely different from your work to help your mind reset."
  },
  {
    question: "Can I pause a Pomodoro timer?",
    answer: "Traditional Pomodoro Technique suggests not pausing timers, as interruptions break focus. However, our timer does allow pausing for unavoidable interruptions like urgent calls or bathroom breaks. Try to minimize pauses and restart quickly. If you're frequently interrupted, consider addressing the source of interruptions or adjusting your work environment for better focus."
  },
  {
    question: "How many Pomodoros should I do per day?",
    answer: "Most people complete 8-12 Pomodoros per day, which equals 4-6 hours of focused work. Start with 4-6 Pomodoros if you're new to the technique, then gradually increase as you build focus stamina. Quality matters more than quantity – it's better to have fewer, highly focused sessions than many distracted ones."
  },
  {
    question: "Is this Pomodoro timer really free?",
    answer: "Yes! This tool is 100% free to use with no hidden costs, subscriptions, or premium features. There are no ads, no account required, and all features are available immediately. We believe productivity tools should be accessible to everyone, so this timer will always remain completely free."
  },
  {
    question: "What if I get interrupted during a Pomodoro?",
    answer: "If you can't avoid the interruption, pause the timer, handle the interruption quickly, then resume. For minor internal distractions (like remembering something), jot it down on paper and continue working – deal with it during your break. If interruptions are frequent, try using 'Do Not Disturb' signs, closing unnecessary apps, or communicating your focus time to others."
  },
  {
    question: "Can I customize the session length?",
    answer: "Yes! While the traditional Pomodoro is 25 minutes, you can adjust work sessions, short breaks, and long breaks to match your personal productivity style. Some people work better with 45-50 minute sessions, while others prefer 15-20 minutes. Experiment to find what works best for your attention span and type of work."
  },
  {
    question: "Do I need to download anything?",
    answer: "No downloads required! This is a web-based timer that works instantly in any modern browser on desktop, mobile, or tablet. Simply bookmark this page and access it whenever you need to focus. It works offline too, so you can use it even without an internet connection after the initial page load."
  },
  {
    question: "Can I use this Pomodoro timer on my phone?",
    answer: "Absolutely! The timer is fully mobile-responsive and works perfectly on smartphones and tablets. You can add it to your phone's home screen for quick access. The timer will continue running even if you switch between apps, making it perfect for mobile productivity on the go."
  },
  {
    question: "Does this timer save my progress?",
    answer: "No, this tool is privacy-friendly and doesn't store your data, track your activity, or require any personal information. Each session is independent. If you want to track your productivity over time, we recommend keeping a simple journal or using your preferred note-taking app alongside the timer."
  },
  {
    question: "Is 25 minutes the best Pomodoro length?",
    answer: "The 25-minute standard is based on research showing it's long enough for deep focus but short enough to maintain attention. However, the best length depends on your work type and attention span. Complex tasks might benefit from longer sessions (45-90 minutes), while repetitive tasks work well with shorter intervals (15-20 minutes). Experiment to find your optimal focus duration."
  }
];

// Generate FAQ schema for SEO
export function generateFAQSchema(faqs: FAQItem[]) {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
}